import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  Checkbox,
  CircularProgress,
  ListItemText,
  Tooltip,
  styled,
  Typography,
} from "@mui/material";
import { LuChevronDown } from "react-icons/lu";
import { getIconComponent } from "@/Utils/getIconComponent";
import FormLabel from "./FormLabel";
import axiosInstance from "@/Api/AxiosInstance";

const CustomCheckbox = styled(Checkbox)(() => ({
  color: "var(--brand-600)",
  borderRadius: "6px",
  "&.Mui-checked": {
    color: "var(--brand-800)",
  },
  "& .MuiSvgIcon-root": {
    borderRadius: "10px",
    fontSize: "18px",
  },
  "&:hover": {
    backgroundColor: "var(--brand-50)",
  },
}));

export default function MultiSelectDropdown({
  label = "",
  placeholder = "Select...",
  options = [],
  value = [],
  onChange,
  sx = {},
  error = false,
  helperText = "",
  dropdownIconName = LuChevronDown,
  iconColor = "var(--gray-400)",
  width,
  height,
  gap,
  multiple = true,
  ...props
}) {
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const DropdownIcon = dropdownIconName
    ? getIconComponent(dropdownIconName, { size: 20, color: iconColor })
    : null;

  const normalizedValue = Array.isArray(value) ? value : [];

  const fetchOptions = async () => {
    if (
      options &&
      typeof options === "object" &&
      !Array.isArray(options) &&
      options.url
    ) {
      try {
        setLoading(true);
        const response = await axiosInstance({
          method: options.method || "GET",
          url: options.url,
        });

        const orgs = response?.data?.organizations ?? [];
        const mapped = orgs.map((item) => ({
          label: item.organizationName,
          value: item.id,
        }));
        setFetchedOptions(mapped);
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        setFetchedOptions([]);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (Array.isArray(options)) {
      setFetchedOptions(options);
    } else {
      fetchOptions();
    }
  }, [JSON.stringify(options)]);

  const handleChange = (event) => {
    const selected = event.target.value;
    onChange && onChange(selected);
  };

  const renderValue = (selected) => {
    if (!selected || selected.length === 0) {
      return <span style={{ color: "var(--brand--800)" }}>{placeholder}</span>;
    }

    const labels = selected
      .map((val) => {
        const opt = fetchedOptions.find((o) => o.value === val);
        return opt?.label;
      })
      .filter(Boolean);

    return (
      <Tooltip title={labels.join(", ")}>
        <Typography
          variant="bodySemiBold"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            verticalAlign: "middle",
            width: "100%",
            color:
              normalizedValue.length === 0
                ? "var(--gray-400)"
                : "var(--brand-900)",
          }}
        >
          {`Selected ${labels.length} ${label}`}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box
      gap={label ? 1 : 0}
      sx={{ display: "flex", flexDirection: "column", width: width, ...sx }}
    >
      {label && <FormLabel label={label} gap={gap} />}
      <TextField
        select
        size="small"
        value={normalizedValue}
        onChange={handleChange}
        error={error}
        helperText={helperText}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue,
          IconComponent: () => null,
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                border: "1px solid var(--gray-300)",
                "& .MuiOutlinedInput-root": {
                  height: height || 40,
                  fontSize: "16px",
                  alignItems: "center",
                  color: "#3f00b7", // Always blue text
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#3f00b7", // Always blue text
                    fontWeight: 600,
                },
                },

                "& .MuiMenuItem-root": {
                  borderRadius: 1,
                  fontSize: 16,
                  "&.Mui-selected": {
                    backgroundColor: "var(--brand-100)",
                    
                  },
                  "&:hover": {
                    backgroundColor: "var(--brand-50)",
                  },
                },
              },
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <>
              {loading && (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              )}
              {DropdownIcon && (
                <InputAdornment position="end">{DropdownIcon}</InputAdornment>
              )}
            </>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: height || 40,
            fontSize: "16px",
            alignItems: "center",
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color:
                normalizedValue.length === 0
                  ? "var(--gray-400)"
                  : "var(--brand-900)",
            },
            "& fieldset": {
              borderRadius: "8px",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--brand-600)",
            },
            "&:hover fieldset": {
              borderColor: "var(--gray-400)",
            },
          },
        }}
        {...props}
      >
        {/* Styled dropdown heading */}
        <Box
          sx={{
            px: 2,
            py: 1.5,

          }}
        >
          <Typography variant="bodyMedium" color="var(--brand-910)">
            Entity Type
          </Typography>
        </Box>

        {fetchedOptions.map((opt, idx) => (
          <MenuItem
            key={idx}
            value={opt.value}
            sx={{
              display: "flex",
              alignItems: "center",
              height: 36,
              gap: 1.5,
              px: 2,      
              // py: 1,     
              ml:1,
            }}
          >
            <CustomCheckbox
              checked={normalizedValue.includes(opt.value)}
              size="small"
              sx={{ p: 0.5 }}
            />
            <ListItemText
              disableTypography
              primary={
                <Typography variant="bodyBase" color="var(--brand-910)">
                  {opt.label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
